import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Link from 'react-router-dom/Link';
import { ifProp, palette as toolPalette } from 'styled-tools';
import { color } from 'styled-system';

const propTypes = {
  bordered: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  coloringType: color.propTypes.color,
  disabled: PropTypes.bool,
  dataName: PropTypes.string,
  rounded: PropTypes.bool,
  palette: PropTypes.string,
  sm: PropTypes.bool,
  transparent: PropTypes.bool,
  type: PropTypes.string,
  to: PropTypes.string,
  href: PropTypes.string,
  xs: PropTypes.bool,
  external: PropTypes.bool,
};

const defaultProps = {
  bordered: false,
  children: undefined,
  className: undefined,
  coloringType: undefined,
  disabled: false,
  dataName: null,
  rounded: false,
  transparent: false,
  palette: 'primaryButton',
  sm: false,
  type: 'button',
  to: undefined,
  href: undefined,
  xs: false,
  external: false,
};

function backgroundColor({ bordered, disabled, palette, transparent, coloringType }) {
  if (!palette) return undefined;
  if (bordered) return 'transparent';
  if (transparent) return 'transparent';
  return toolPalette(palette, disabled ? 2 : 0);
}

function borderColor({ bordered, disabled, palette, transparent, coloringType }) {
  if (!bordered) return 'transparent';
  if (transparent) return 'transparent';
  return toolPalette(palette, disabled ? 1 : 0);
}

function foregroundColor({ bordered, disabled, palette, transparent, coloringType }) {
  if (!palette) return undefined;
  if (transparent) return toolPalette(palette, disabled ? 2 : 1);
  if (!bordered) return toolPalette('white', 0);
  return toolPalette(palette, 0);
}

function hoverBackgroundColor({ bordered, disabled, transparent, palette }) {
  if (!palette) return undefined;
  if (bordered) return 'transparent'
  return !disabled && !transparent && toolPalette(palette, 1);
}

function hoverForegroundColor({ bordered, disabled, transparent, palette }) {
  if (!palette) return undefined;
  if (bordered) return toolPalette(palette, 1);
  return !disabled && transparent && toolPalette(palette, 0);
}
function hoverBorderColor({ disabled, transparent, palette }) {
  if (!palette) return undefined;
  if (transparent) return 'transparent';
  return !disabled && toolPalette(palette, 1);
}

function Button({
  bordered,
  children,
  className,
  dataName,
  disabled,
  href,
  palette,
  sm,
  to,
  transparent,
  type,
  xs,
  external,
  ...props
}) {
  if (to) {
    return (
      <StyledLink className={className} to={to} {...props}>
        {children}
      </StyledLink>
    );
  }
  if (href) {
    return (
      <StyledA className={className} href={href} target={external ? "_blank" : "_self"} {...props}>
        {children}
      </StyledA>
    );
  }
  return (
    <button className={className} data-name={dataName} {...props} type={type}>
      {children}
    </button>
  );
}

const borderedStyle = css`
  box-shadow: none;
  color: ${foregroundColor};
  background-color: ${backgroundColor};
  border: 1px solid ${borderColor};

  &:hover {
    background-color: ${hoverBackgroundColor};
    border-color: ${hoverBorderColor};
    color: ${hoverForegroundColor};
  }
`;

const styles = css`
  box-sizing: border-box;
  display: inline-flex;
  justify-content: center;
  padding: 0.625em 2em;
  align-items: center;
  white-space: nowrap;
  height: 2.75em;
  appearance: none;
  text-decoration: none;
  outline: none;
  font-size: var(--font-size-base);
  line-height: 1.42857;
  color: #fff;
  border: none;
  pointer-events: ${ifProp('disabled', 'none', 'auto')};

  background-color: ${backgroundColor};
  cursor: ${ifProp('disabled', 'default', 'pointer')};
  color: ${foregroundColor};

  &:hover {
    background-color: ${hoverBackgroundColor};
    color: ${hoverForegroundColor};
  }

  &:focus {
    outline: none;
  }

  ${ifProp('bordered', borderedStyle)};
  opacity: ${ifProp('disabled', 0.5, 1)};
`;

const xsStyle = css`
  padding: 2px 10px;
  height: 2em;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  box-shadow: none;
`;

const smStyle = css`
  width: 100%;
  font-size: var(--font-size-xs);
`;
const StyledLink = styled(Link)`
  text-decoration: none;
`;
const StyledA = styled.a`
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;


const StyledButton = styled(Button).attrs({
  palette: ({ palette }) => palette || 'primaryButton',
  type: ({ type }) => type || 'button',
})`
  ${styles};
  ${ifProp('xs', xsStyle)};
  ${ifProp('sm', smStyle)};
`;

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default StyledButton;
